/*
 * Copyright (C) 2023 Das Land Schleswig-Holstein vertreten durch den
 * Ministerpräsidenten des Landes Schleswig-Holstein
 * Staatskanzlei
 * Abteilung Digitalisierung und zentrales IT-Management der Landesregierung
 *
 * Lizenziert unter der EUPL, Version 1.2 oder - sobald
 * diese von der Europäischen Kommission genehmigt wurden -
 * Folgeversionen der EUPL ("Lizenz");
 * Sie dürfen dieses Werk ausschließlich gemäß
 * dieser Lizenz nutzen.
 * Eine Kopie der Lizenz finden Sie hier:
 *
 * https://joinup.ec.europa.eu/collection/eupl/eupl-text-eupl-12
 *
 * Sofern nicht durch anwendbare Rechtsvorschriften
 * gefordert oder in schriftlicher Form vereinbart, wird
 * die unter der Lizenz verbreitete Software "so wie sie
 * ist", OHNE JEGLICHE GEWÄHRLEISTUNG ODER BEDINGUNGEN -
 * ausdrücklich oder stillschweigend - verbreitet.
 * Die sprachspezifischen Genehmigungen und Beschränkungen
 * unter der Lizenz sind dem Lizenztext zu entnehmen.
 */
import { ApiRootService } from '@common/api-root-shared';
import { BinaryFileActions, BinaryFileListResource, LoadBinaryFileListProps } from '@common/binary-file-shared';
import {
  CommandActions,
  CommandListResource,
  CommandOrder,
  CreateCommandSuccessProps,
  LoadCommandListProps,
} from '@alfa-client/command-shared';
import { ApiError, BlobWithFileName, isNotUndefined, isServiceUnavailable } from '@common/tech-shared';
import { SnackBarService } from '@ui';
import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Action, Store } from '@ngrx/store';
import { hasLink } from '@ngxp/rest';
import { Observable, of } from 'rxjs';
import { catchError, filter, first, map, mergeMap, switchMap, tap } from 'rxjs/operators';
import { VorgangListService } from '../vorgang-list.service';
import { VorgangNavigationUtil } from '../vorgang-navigation.util';
import { VorgangWithEingangLinkRel } from '../vorgang.linkrel';
import { VorgangMessages } from '../vorgang.messages';
import { AdditionalActions, VorgangWithEingangResource } from '../vorgang.model';
import { createAssignUserCommand, createUnassignUserCommand } from '../vorgang.util';
import {
  HttpErrorAction,
  SearchVorgaengeByProps,
  VorgangActions,
  VorgangExportAction,
  VorgangUriWithContextProps,
  VorgangWithEingangAction,
} from './vorgang.actions';
import { VorgangFacade } from './vorgang.facade';
import { VorgangRepository } from './vorgang.repository';
import { VorgangSelectors } from './vorgang.selectors';

@Injectable()
export class VorgangEffects {
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);
  private readonly repository = inject(VorgangRepository);
  private readonly apiRootService = inject(ApiRootService);
  private readonly vorgangFacade = inject(VorgangFacade);
  private readonly snackbarService = inject(SnackBarService);

  //VorgangList
  loadVorgangList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VorgangActions.loadVorgangList),
      concatLatestFrom(() => this.store.select(VorgangSelectors.lastSelectedVorgangPageToLoad)),
      switchMap(([props, page]) =>
        this.repository.loadVorgangList(props.apiRoot, props.linkRel, page).pipe(
          map((loadedVorgangList) => VorgangActions.loadVorgangListSuccess({ vorgangList: loadedVorgangList })),
          catchError((error: HttpErrorResponse) =>
            of(
              VorgangActions.loadVorgangListFailure({
                apiError: error.error,
              }),
            ),
          ),
        ),
      ),
    ),
  );

  loadNextPage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VorgangActions.loadNextPage),
      concatLatestFrom(() => this.store.select(VorgangSelectors.vorgangListLast)),
      switchMap(([, vorgangList]) =>
        this.repository.getNextVorgangListPage(vorgangList.resource).pipe(
          map((loadedVorgangList) => VorgangActions.loadNextPageSuccess({ vorgangList: loadedVorgangList })),
          catchError((error: HttpErrorResponse) =>
            of(
              VorgangActions.loadVorgangListFailure({
                apiError: error.error,
              }),
            ),
          ),
        ),
      ),
    ),
  );

  loadPrevPage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VorgangActions.loadPrevPage),
      concatLatestFrom(() => this.store.select(VorgangSelectors.vorgangList)),
      switchMap(([, vorgangList]) =>
        this.repository.getPrevVorgangListPage(vorgangList.resource).pipe(
          map((loadedVorgangList) => VorgangActions.loadPrevPageSuccess({ vorgangList: loadedVorgangList })),
          catchError((error: HttpErrorResponse) =>
            of(
              VorgangActions.loadVorgangListFailure({
                apiError: error.error,
              }),
            ),
          ),
        ),
      ),
    ),
  );

  searchVorgaengeBy$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VorgangActions.searchVorgaengeBy),
      switchMap((props: SearchVorgaengeByProps) =>
        this.repository.searchVorgaengeBy(props.apiRoot, props.searchString, props.linkRel).pipe(
          map((loadedVorgangList) => VorgangActions.searchVorgaengeBySuccess({ vorgangList: loadedVorgangList })),
          catchError((error) => of(VorgangActions.searchVorgaengeByFailure({ httpErrorResponse: error }))),
        ),
      ),
    ),
  );

  searchForPreview$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VorgangActions.searchForPreview),
      concatLatestFrom(() => [this.apiRootService.getApiRoot(), this.vorgangFacade.getVorgangFilter()]),
      switchMap(([stringBasedProps, apiRoot, vorgangFilter]) =>
        this.repository
          .searchVorgaengeBy(
            apiRoot.resource,
            stringBasedProps.string,
            VorgangNavigationUtil.getSearchLinkRel(vorgangFilter),
            VorgangListService.SEARCH_PREVIEW_LIST_LIMIT,
          )
          .pipe(
            map((loadedVorgangList) => VorgangActions.searchForPreviewSuccess({ vorgangList: loadedVorgangList })),
            catchError((error) => of(VorgangActions.searchForPreviewFailure({ httpErrorResponse: error }))),
          ),
      ),
    ),
  );

  showSearchError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(VorgangActions.searchForPreviewFailure, VorgangActions.searchVorgaengeByFailure),
        map((action: HttpErrorAction) => this.showSearchError(action.httpErrorResponse)),
      ),
    { dispatch: false },
  );

  showSearchError(error: HttpErrorResponse): void {
    if (isServiceUnavailable(error.status)) {
      this.snackbarService.showError(VorgangMessages.SEARCH_UNAVAILABLE);
    }
  }

  //VorgangWithEingang
  loadVorgangWithEingang$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VorgangActions.loadVorgangWithEingang),
      switchMap((props: VorgangUriWithContextProps) =>
        this.repository.getVorgang(props.resourceUri).pipe(
          mergeMap((vorgangWithEingang) => this.handleSuccessActions(vorgangWithEingang, props.additionalActions)),
          catchError((error) => this.handleFailureActions(error, props.additionalActions)),
        ),
      ),
    ),
  );

  private handleSuccessActions(
    vorgangWithEingang: VorgangWithEingangResource,
    additionalActions: AdditionalActions,
  ): Action<string>[] {
    if (isNotUndefined(additionalActions?.additionalSuccessAction)) {
      return [VorgangActions.loadVorgangWithEingangSuccess({ vorgangWithEingang }), additionalActions.additionalSuccessAction()];
    }
    return [VorgangActions.loadVorgangWithEingangSuccess({ vorgangWithEingang })];
  }

  private handleFailureActions(error: HttpErrorResponse, additionalActions: AdditionalActions): Observable<Action<string>> {
    if (isNotUndefined(additionalActions?.additionalFailureAction)) {
      return of(
        VorgangActions.loadVorgangWithEingangFailure({
          apiError: error.error,
        }),
        additionalActions.additionalFailureAction(error),
      );
    }
    return of(
      VorgangActions.loadVorgangWithEingangFailure({
        apiError: error.error,
      }),
    );
  }

  loadVorgangWithEingangSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VorgangActions.loadVorgangWithEingangSuccess),
      filter((props: VorgangWithEingangAction) => hasLink(props.vorgangWithEingang, VorgangWithEingangLinkRel.PENDING_COMMANDS)),
      switchMap((props: VorgangWithEingangAction) =>
        of(VorgangActions.loadPendingCommandList({ vorgangWithEingang: props.vorgangWithEingang })),
      ),
    ),
  );

  loadPendingCommandList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VorgangActions.loadPendingCommandList),
      switchMap((props: VorgangWithEingangAction) =>
        of(CommandActions.loadCommandList(this.createLoadPendingCommandListProps(props.vorgangWithEingang))),
      ),
    ),
  );

  createLoadPendingCommandListProps(vorgangWithEingang: VorgangWithEingangResource): LoadCommandListProps {
    return {
      resource: vorgangWithEingang,
      linkRel: VorgangWithEingangLinkRel.PENDING_COMMANDS,
      successAction: (commandList: CommandListResource) => VorgangActions.loadPendingCommandListSuccess({ commandList }),
      failureAction: (apiError: ApiError) => VorgangActions.loadPendingCommandListFailure({ apiError }),
    };
  }

  loadRepresentationList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VorgangActions.loadRepresentationList),
      switchMap((props: VorgangWithEingangAction) =>
        of(BinaryFileActions.loadBinaryFileList(this.createLoadRepresentationListProps(props.vorgangWithEingang))),
      ),
    ),
  );

  createLoadRepresentationListProps(vorgangWithEingang: VorgangWithEingangResource): LoadBinaryFileListProps {
    return {
      resource: vorgangWithEingang,
      linkRel: VorgangWithEingangLinkRel.REPRESENTATIONS,
      successAction: (binaryFileList: BinaryFileListResource) => VorgangActions.loadRepresentationListSuccess({ binaryFileList }),
      failureAction: (apiError: ApiError) => VorgangActions.loadRepresentationListFailure({ apiError }),
    };
  }

  loadAttachmentList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VorgangActions.loadAttachmentList),
      switchMap((props: VorgangWithEingangAction) =>
        of(BinaryFileActions.loadBinaryFileList(this.createLoadAttachmentListProps(props.vorgangWithEingang))),
      ),
    ),
  );

  createLoadAttachmentListProps(vorgangWithEingang: VorgangWithEingangResource): LoadBinaryFileListProps {
    return {
      resource: vorgangWithEingang,
      linkRel: VorgangWithEingangLinkRel.ATTACHMENTS,
      successAction: (binaryFileList: BinaryFileListResource) => VorgangActions.loadAttachmentListSuccess({ binaryFileList }),
      failureAction: (apiError: ApiError) => VorgangActions.loadAttachmentListFailure({ apiError }),
    };
  }

  private getExportSuccessActions(props: VorgangExportAction, blobWithFileName: BlobWithFileName): Action[] {
    const saveFileAction = BinaryFileActions.saveFile({
      fileData: blobWithFileName.blob,
      fileName: blobWithFileName.fileName,
    });

    return props.linkRel === VorgangWithEingangLinkRel.DOWNLOAD_EXPORT_VORGANG ?
        [saveFileAction, VorgangActions.exportVorgangSuccess()]
      : [
          saveFileAction,
          CommandActions.createCommand({
            resource: props.vorgangWithEingang,
            linkRel: VorgangWithEingangLinkRel.COMPLETE_EXPORT_VORGANG,
            command: { order: CommandOrder.EXPORT_VORGANG, body: {} },
            snackBarMessage: VorgangMessages.EXPORT_VORGANG_SUCCESS,
            snackBarErrorMessage: VorgangMessages.EXPORT_VORGANG_FAILURE,
          }),
        ];
  }

  exportVorgang$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VorgangActions.exportVorgang),
      switchMap((props: VorgangExportAction) =>
        this.repository.exportVorgang(props.vorgangWithEingang, props.linkRel).pipe(
          mergeMap((blobWithFileName: BlobWithFileName) => this.getExportSuccessActions(props, blobWithFileName)),
          catchError((error: HttpErrorResponse) =>
            of(
              VorgangActions.exportVorgangFailure({
                apiError: error.error,
              }),
            ),
          ),
        ),
      ),
    ),
  );

  exportVorgangCommandSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CommandActions.createCommandSuccess),
      filter((props: CreateCommandSuccessProps) => props.command.order === CommandOrder.EXPORT_VORGANG),
      map(() => VorgangActions.exportVorgangSuccess()),
    ),
  );

  assignUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VorgangActions.assignUser),
      switchMap(({ userUri }) =>
        this.store.select(VorgangSelectors.vorgangWithEingang).pipe(
          first(),
          tap((vorgangWithEingang) => {
            this.store.dispatch(
              CommandActions.createCommand({
                resource: vorgangWithEingang.resource,
                linkRel: VorgangWithEingangLinkRel.ASSIGN,
                command: createAssignUserCommand(userUri),
              }),
            );
          }),
          map(() => VorgangActions.assignUserSuccess()),
        ),
      ),
    ),
  );

  unassignUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VorgangActions.unassignUser),
      switchMap(() =>
        this.store.select(VorgangSelectors.vorgangWithEingang).pipe(
          first(),
          tap((vorgangWithEingang) => {
            this.store.dispatch(
              CommandActions.createCommand({
                resource: vorgangWithEingang.resource,
                linkRel: VorgangWithEingangLinkRel.UNASSIGN,
                command: createUnassignUserCommand(),
              }),
            );
          }),
          map(() => VorgangActions.unassignUserSuccess()),
        ),
      ),
    ),
  );
}
